import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useAuthorization } from './hooks/useAuthorization'
import { useSmartTranslation } from './hooks/useSmartTranslation'
import { selectFirstAvailableTab } from './utilities/navigator/selectFirstAvailableTab'
import { requestUnsavedChangesConfirmation } from './utilities/unsavedChangesGuard'

// Import typedefs from another file
/**
 * @typedef {import('./utilities/objectStructures/navigator/componentsInfo').componentsInfo} componentsInfo
 */

function TabPanel (props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3} sx={{ backgroundColor: theme => `${theme.palette.background.paper} !important` }}>
          <Typography component='span'>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
}

function a11yProps (index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`
  }
}

/**
 *
 * @param {Object} props Props object
 * @param {componentsInfo} props.componentsInfo
 * @param {Number} props.selected Selected index
 * @param {Function} props.setSelected Set selected index
 * @returns
 */
const SmartNavigator = ({ componentsInfo, selected, setSelected, sticky = true, stickyTop = 0, stickyZIndex = 30, stickyHeight = 48 }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const isAuthenticated = useAuthorization()
  const navigatorRef = useRef(null)
  const resizeFrameRef = useRef(null)
  const [tabsViewportWidth, setTabsViewportWidth] = useState(null)

  const initialIndex = selectFirstAvailableTab(componentsInfo, isAuthenticated, selected)
  const [selectedIndex, setSelectedIndex] = useState(initialIndex ?? 0)

  const handleChangeSelection = async (event, newValue) => {
    if (newValue === selectedIndex) return

    const changeSelection = () => {
      setSelectedIndex(newValue)
      if (setSelected) {
        setSelected(newValue)
      }
    }

    if (componentsInfo[selectedIndex].onChange && typeof componentsInfo[selectedIndex].onChange === 'function') {
      const confirmChange = () => componentsInfo[selectedIndex].onChange(newValue)
      if (requestUnsavedChangesConfirmation(confirmChange)) {
        confirmChange()
      }
      return
    }

    if (requestUnsavedChangesConfirmation(changeSelection)) {
      changeSelection()
    }
  }

  useEffect(() => {
    const preferred = selected ?? selectedIndex
    const idx = selectFirstAvailableTab(componentsInfo, isAuthenticated, preferred)
    if (idx !== null && idx !== selectedIndex) {
      setSelectedIndex(idx)
    }
    if (idx !== null && setSelected && idx !== selected) {
      setSelected(idx)
    }
  }, [componentsInfo, isAuthenticated, selected, selectedIndex, setSelected])

  useLayoutEffect(() => {
    if (!navigatorRef.current) return

    const findScrollableParent = (node) => {
      let current = node?.parentElement
      while (current) {
        const style = window.getComputedStyle(current)
        const overflowX = style.overflowX
        const overflow = style.overflow
        if (['auto', 'scroll', 'overlay'].includes(overflowX) || ['auto', 'scroll', 'overlay'].includes(overflow)) {
          return current
        }
        current = current.parentElement
      }
      return null
    }

    const scrollParent = findScrollableParent(navigatorRef.current)
    if (!scrollParent) return

    const updateWidth = () => {
      const style = window.getComputedStyle(scrollParent)
      const paddingLeft = parseFloat(style.paddingLeft) || 0
      const paddingRight = parseFloat(style.paddingRight) || 0
      const nextWidth = Math.max(0, Math.floor(scrollParent.clientWidth - paddingLeft - paddingRight))

      setTabsViewportWidth(prevWidth => (prevWidth === nextWidth ? prevWidth : nextWidth))
    }

    updateWidth()
    // eslint-disable-next-line no-undef
    const observer = new ResizeObserver(() => {
      if (resizeFrameRef.current !== null) {
        // eslint-disable-next-line no-undef
        cancelAnimationFrame(resizeFrameRef.current)
      }
      // eslint-disable-next-line no-undef
      resizeFrameRef.current = requestAnimationFrame(updateWidth)
    })
    observer.observe(scrollParent)
    observer.observe(navigatorRef.current)
    window.addEventListener('resize', updateWidth)
    return () => {
      if (resizeFrameRef.current !== null) {
        // eslint-disable-next-line no-undef
        cancelAnimationFrame(resizeFrameRef.current)
      }
      observer.disconnect()
      window.removeEventListener('resize', updateWidth)
    }
  }, [sticky])

  return (
    <div
      ref={navigatorRef}
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        '--smart-navigator-sticky-offset': sticky ? `${stickyHeight}px` : '0px'
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          left: 0,
          ...(sticky && {
            top: stickyTop,
            zIndex: stickyZIndex
          }),
          backgroundColor: theme.palette.background.paper,
          width: tabsViewportWidth ? `min(${tabsViewportWidth}px, 100%)` : '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}
      >
        <Tabs
          value={selectedIndex}
          onChange={handleChangeSelection}
          variant='fullWidth'
          aria-label='full width tabs example'
          sx={{ width: '100%' }}
        >
          {
            componentsInfo.map((componentInfo, index) => {
              return (
                <Tab
                  sx={{
                    backgroundColor: (theme) => `${theme.palette.background.color_212121} !important`,
                    minWidth: 0,
                    px: { xs: 0.75, sm: 1, md: 1.5 },
                    fontSize: 'clamp(0.62rem, 0.95vw, 0.95rem)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  key={index}
                  label={componentInfo.title}
                  {...componentInfo.to !== undefined ? { to: componentInfo.to, component: Link } : {}}
                  {...a11yProps(index)}
                  disabled={componentInfo.disableTab ? true : componentInfo.permissionCode === undefined ? false : !isAuthenticated(componentInfo.permissionCode)}
                />
              )
            })
          }
        </Tabs>
      </Box>

      {(() => {
        const hasPermission = componentsInfo.some((componentInfo) =>
          componentInfo.permissionCode === undefined || isAuthenticated(componentInfo.permissionCode)
        )
        if (!hasPermission) {
          return (
            <Box p={3}>
              <Typography variant='h6' align='center'>{t_('No available tabs')}</Typography>
            </Box>
          )
        }
        return componentsInfo.map((componentInfo, index) => {
          return (
            <TabPanel
              key={index}
              value={selectedIndex}
              index={index}
              dir={theme.direction}
              sx={{ backgroundColor: theme => theme.palette.background.paper }}
            >
              {componentInfo?.permissionCode === undefined
                ? componentInfo.component
                : (!isAuthenticated(componentInfo.permissionCode)
                    ? (<Typography variant='h6' align='center'>{t_('Permission denied')}</Typography>)
                    : (componentInfo.component))}
            </TabPanel>
          )
        })
      })()}
    </div>
  )
}

export default SmartNavigator
